import React, { useState, useEffect } from "react";
import { ResetPasswordSchema } from "../../../components/Helpers";
import { Formik, Form, Field } from "formik";
import { FaRegEyeSlash, FaEye } from "react-icons/fa";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  requestResetPassword,
  setResetStatePassword,
} from "../../../Redux/LoginState/LoginActionCreator";
import TextInput from "../../../components/TextInput";
import Uni_Logo from "../../../Assets/DataCloud.jpeg";
import Swal from "sweetalert2";

function ResetForm() {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
    useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [missingCriteria, setMissingCriteria] = useState([]);
  const dispatch = useDispatch();
  const { token } = useParams();
  const navigate = useNavigate();
  const resetPasswordState = useSelector((state) => state.ResetPasswordReducer);

  const checkPasswordStrength = (password) => {
    const criteria = [
      { test: (pw) => /[a-z]/.test(pw), message: "Include a lowercase letter" },
      {
        test: (pw) => /[A-Z]/.test(pw),
        message: "Include an uppercase letter",
      },
      { test: (pw) => /\d/.test(pw), message: "Include a number" },
      {
        test: (pw) => /[^a-zA-Z\d\s:?,]/.test(pw),
        message: "Include a special character",
      }, // Exclude "?"
      { test: (pw) => pw.length >= 8, message: "At least 8 characters long" },
    ];

    const unmetCriteria = criteria
      .filter((criterion) => !criterion.test(password))
      .map((criterion) => criterion.message);

    setMissingCriteria(unmetCriteria);
    setPasswordStrength(criteria.length - unmetCriteria.length);
  };

  const renderStrengthBar = () => {
    if (missingCriteria.length === 0) return null;

    const colors = [
      "bg-red-500",
      "bg-yellow-500",
      "bg-primary",
      "bg-green-500",
    ];
    const width = `${(passwordStrength / 5) * 100}%`;

    return (
      <div className="relative h-2 w-full bg-gray-200 rounded mt-2">
        <div
          className={`absolute top-0 left-0 h-2 rounded transition-all ${
            colors[passwordStrength - 1] || "bg-red-500"
          }`}
          style={{ width }}
        ></div>
      </div>
    );
  };

  const resetPassword = (values) => {
    dispatch(requestResetPassword(values));
  };

  useEffect(() => {
    if (
      resetPasswordState?.resetResponse &&
      !resetPasswordState?.resetLoading
    ) {
      setIsSubmitted(true);
      toast.success("Password reset has been done sucessfully");
      navigate("/login");
      dispatch(setResetStatePassword());
    }
    if (resetPasswordState?.resetError && !resetPasswordState?.resetLoading) {
      const errorMessage = resetPasswordState?.resetError?.errorData?.data?.message;

       if (errorMessage) {
              Swal.fire({
                title: errorMessage,
                icon: "warning",
                showCancelButton: false,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Ok",
                backdrop: `rgba(0, 0, 0, 0.5)`,
              }).then((result) => {
                if (result.isConfirmed) {
                  dispatch(setResetStatePassword());
                }
              });
            } else {
              toast.error(resetPasswordState?.resetError?.message || "unable to reset password");
              dispatch(setResetStatePassword());
            }  
    }
  }, [resetPasswordState]);

  return (
    <div className="flex items-center justify-center w-[80vw] sm:w-[60vw] md:w-[20vw] p-3 md:px-5 bg-white rounded-2xl h-[50vh] sm:h-[55vh]">
      {isSubmitted ? (
        <p className="text-center text-lg font-medium">
          Password Reset Successful. Please{" "}
          <Link className="text-primary hover:underline" to={"/login"}>
            Click Here
          </Link>{" "}
          to login.
        </p>
      ) : (
        <Formik
          initialValues={{
            newPassword: "",
            confirmPassword: "",
            token: token,
          }}
          validationSchema={ResetPasswordSchema}
          onSubmit={(values) => {
            if (values.newPassword === values.confirmPassword) {
              resetPassword(values);
            } else {
              toast.error("Passwords do not match");
              dispatch(setResetStatePassword());
            }
          }}
        >
          {({ errors, touched, values, handleChange }) => (
            <Form className="shadow-none w-full h-full mt-5 sm:mt-10">
               <div className="text-center mb-8 sm:mb-8">
               <div className="flex items-center justify-center mb-4">
                <img src={Uni_Logo} alt="Logo" className="h-28 sm:h-32 w-auto object-contain hover:scale-105 transition-transform duration-300" />      
              </div>
                
                <p className="text-xl flex items-center justify-center mb-10 sm:mb-10">
                  Reset Your Password
                </p>
              </div>

              {/* New Password Field */}
              <div className=" relative my-8">
                <TextInput
                  name="newPassword"
                  type={isPasswordVisible ? "text" : "password"}
                  placeholder="New Password"
                  handleChange={handleChange}
                  value={values.newPassword}
                  className="w-full border border-gray-300 rounded-[0.3rem] p-1 h-[2.4rem]"
                  onKeyUp={(e) => checkPasswordStrength(e.target.value)}
                  legend
                />

                <div
                  className="absolute  right-0 top-3 flex items-center px-2 cursor-pointer"
                  onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                >
                  {!isPasswordVisible ? <FaRegEyeSlash /> : <FaEye />}
                </div>

                {errors.newPassword && touched.newPassword && (
                  <p className="text-red-400 ">{errors.newPassword}</p>
                )}

                {/* Password Strength Bar */}
                {values.newPassword && renderStrengthBar()}

                {/* Missing Criteria */}
                {values.newPassword && missingCriteria.length > 0 && (
                  <ul className="mt-2 text-sm text-gray-600">
                    {missingCriteria.map((criteria, index) => (
                      <li key={index} className="text-red-500">
                        {criteria}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Confirm Password Field */}
              <div className="relative my-8">
                <TextInput
                  name="confirmPassword"
                  value={values.confirmPassword}
                  placeholder="Confirm Password"
                  type={isConfirmPasswordVisible ? "text" : "password"}
                  handleChange={handleChange}
                  className="w-full border border-gray-300 rounded-[0.3rem] p-2 h-[2.4rem]"
                  legend
                />
                 <div
                  className="absolute  right-0 top-3 flex items-center px-2 cursor-pointer"
                  onClick={() => setIsConfirmPasswordVisible(!isConfirmPasswordVisible)}
                >
                  {!isConfirmPasswordVisible ? <FaRegEyeSlash /> : <FaEye />}
                </div>
               
                {errors.confirmPassword && touched.confirmPassword && (
                  <p className="text-red-400">
                    {errors.confirmPassword}
                  </p>
                )}
              </div>

              <button
                type="submit"
                className="w-full bg-primary text-white py-2 rounded font-medium hover:bg-primaryDark"
              >
                Submit
              </button>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
}

export default ResetForm;
